"use client";

/**
 * Sign-in gate for a FRESH (paid) search (2026-08-22).
 *
 * Rendered by the /compare server page when the visitor is signed out AND the
 * query is NOT already a saved page (a cache hit is served openly, upstream).
 * Saved/cached comparison pages stay free to browse for everyone; only a brand
 * new price search requires an account.
 *
 * Shows the EXISTING MioAuthDialog popup, auto-opened. On successful sign-in the
 * dialog fires onAuthenticated FIRST (before it closes), so we re-run the same
 * /compare?q=<query> render via router.refresh(): it now passes the gate and
 * starts the search. Only a genuine cancel (closed without signing in) sends the
 * visitor home. The backend middleware enforces the same rule, so a robot that
 * skips this popup and calls the search API directly is still refused.
 */
import { useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { MioAuthDialog } from "@/components/auth/MioAuthDialog";
import { trackSignupPromptShown } from "@/lib/analytics";
import type { AuthMode } from "@/components/auth/useAuthFlow";

export function SignInToSearchGate({ query }: { query: string }) {
  const router = useRouter();
  const [open, setOpen] = useState(true); // auto-open the existing popup
  const [mode, setMode] = useState<AuthMode>("sign-up");
  const authedRef = useRef(false);
  const shownRef = useRef(false);

  // 2026-10-01 (owner): measure the wall. One event when the screen appears,
  // with the search the visitor typed; it also leaves the origin marker, so a
  // sign-up or sign-in finished from here is recorded as source
  // "search_wall". Shown minus finished = the visitors who left at the wall.
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    trackSignupPromptShown({ source: "search_wall", pageKind: "compare_streaming", query });
  }, [query]);

  function handleAuthenticated() {
    authedRef.current = true;
    // Re-run the server render for this same /compare?q=... URL. Now signed in
    // (setActive has run before this callback), it passes the gate and starts
    // the search. router.refresh() re-fetches with the new Clerk session.
    router.refresh();
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) return;
    // Closed. A successful sign-in already kicked off the search above; only a
    // genuine cancel (closed without signing in) goes home.
    if (!authedRef.current) router.push("/");
  }

  return (
    <MioAuthDialog
      open={open}
      mode={mode}
      onOpenChange={handleOpenChange}
      onModeChange={setMode}
      onAuthenticated={handleAuthenticated}
    />
  );
}
